/**
 * exportPaymentCSV — Per-payment CSV export utility
 *
 * Fetches all orders linked to a specific payment (invoice) from the backend
 * and triggers a browser file download of the resulting CSV.
 *
 * CSV columns (server-generated):
 *   Order ID, Barcode, Name, Phone, Address, Status, Content,
 *   Payment Status, Total, City, Integration Barcode, External Barcode,
 *   Source, Created At, Delivered At
 *
 * Edge cases handled:
 *   - Empty order list (server returns 404 → meaningful error message)
 *   - Network errors (rejected promise with descriptive message)
 *   - Missing fields (server leaves them blank, not null or "N/A")
 *
 * @param {number|string} paymentId - The ID of the payment/invoice to export
 * @returns {Promise<void>} Resolves on successful download, rejects with an Error on failure
 */
import axios from 'axios'

export default async function exportPaymentCSV(paymentId) {
  if (!paymentId && paymentId !== 0) {
    throw new Error('paymentId is required')
  }

  let response
  try {
    response = await axios.get(`api/invoices/${paymentId}/export-orders-csv/`, {
      responseType: 'blob',
    })
  } catch (error) {
    // Try to extract a meaningful error message from the response
    if (error.response) {
      // Server responded with an error status
      if (error.response.data instanceof Blob) {
        // The error body is a Blob (because we requested responseType: 'blob')
        // Read it as text to extract the JSON error message
        const text = await error.response.data.text()
        try {
          const parsed = JSON.parse(text)
          throw new Error(parsed.error || 'Erreur lors de l\'export')
        } catch (parseErr) {
          if (parseErr.message && !parseErr.message.includes('JSON')) {
            throw parseErr
          }
        }
      }
      throw new Error(
        error.response.data?.error || `Erreur serveur (${error.response.status})`,
      )
    }
    throw new Error('Erreur réseau — vérifiez votre connexion')
  }

  // Trigger browser download
  const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' })
  const url = window.URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.setAttribute('download', `payment_${paymentId}.csv`)
  document.body.appendChild(link)
  link.click()
  link.remove()
  window.URL.revokeObjectURL(url)
}
